import { tenantApi } from '@shared/api/tenantApi';
import { unwrap, unwrapList } from '@shared/api/unwrap';
import type { AccessInvite, MemberInviteCredentials } from './api';

/**
 * The OpenItem member features beyond a PIN for one person, as Glovent had them: deliveries, guest
 * stays, goods removal, saved groups, regulars kept by the gate, PINs the guard made, and opening
 * the gate by scanning its QR code. Each is switched on per property by management.
 */

/** What a property's gate system lets the member do; null on a property that isn't on OpenItem. */
export interface MemberAppFeatures {
  deliveries: boolean;
  /** Up to four couriers shown as chips before the full list. */
  featured_couriers: string[];
  guest_stays: boolean;
  guest_stay_max_nights: number;
  /** Whether a stay can be booked for several guests at once, and how many. */
  guest_stay_groups: boolean;
  guest_stay_group_min: number;
  guest_stay_group_max: number;
  goods_removal: boolean;
  groups: boolean;
  group_min: number;
  group_max: number;
  regulars: boolean;
  regular_max_days: number;
  regular_start_within_days: number;
  scan_to_open: boolean;
}

/** A PIN the gate system holds for the member's property, as OpenItem lists them. */
export interface GatePin {
  case_id: string;
  visitor_name: string | null;
  visitor_phone: string | null;
  pin: string | null;
  share_message: string | null;
  created_at: string | null;
  valid_from: string | null;
  valid_until: string | null;
  /** Null when the guard made it at the gate rather than the member in the app. */
  linked_access_invite_id: string | null;
}

export type BookingPayload = { location_id: string; start_date: string; end_date: string } & (
  | { kind: 'delivery' | 'collection'; courier: string; reference?: string; driver_phone?: string }
  | { kind: 'guest_stay'; member_contact_id: string; adults: number; children: number }
);

export interface GoodsRemovalPayload {
  location_id: string;
  /** YYYY-MM-DD. */
  date: string;
  person_name: string;
  description: string;
  reference?: string;
  photo: File;
}

export interface VisitorGroup {
  access_visitor_group_id: string;
  name: string;
  members: { member_contact_id: string; display_name: string; phone: string | null }[];
}

export interface VisitorGroupPayload {
  name: string;
  member_contact_ids: string[];
}

export type GateRegularStatus = 'waiting' | 'approved' | 'paused';

/** A regular kept by the gate: the days asked for, and where the gate stands on them. */
export interface GateRegular {
  /** ISO weekdays, 1 = Monday ... 7 = Sunday. */
  days: number[];
  start_date: string;
  end_date: string;
  after_hours: boolean;
  status: GateRegularStatus;
  /** Their one PIN, once the gate approves them. */
  pin: string | null;
  share_message: string | null;
}

export interface GateRegularPayload {
  location_id: string;
  days: number[];
  start_date: string;
  end_date: string;
  after_hours: boolean;
  paused: boolean;
}

export const VISITOR_GROUPS_QUERY_KEY = ['visitor-groups'] as const;

export const gatePinsQueryKey = (locationId: string) => ['access-gate-pins', locationId] as const;

export const gateRegularQueryKey = (contactId: string, locationId: string) =>
  ['gate-regular', contactId, locationId] as const;

/** Making a PIN goes through the gate system, which can be slower than the default timeout. */
const GATE_TIMEOUT = 60_000;

type BookingResult = { invite: AccessInvite; credentials?: MemberInviteCredentials };

function bookingResult(body: { data: AccessInvite; credentials?: MemberInviteCredentials }): BookingResult {
  return { invite: unwrap(body), credentials: body.credentials };
}

/** The goods photo on the member's own goods removal, to share with the PIN. */
export async function fetchGoodsPhoto(inviteId: string): Promise<Blob> {
  const { data, headers } = await tenantApi.get<ArrayBuffer>(`/public/me/access-invites/${inviteId}/goods-photo`, {
    responseType: 'arraybuffer',
  });
  return new Blob([data], { type: String(headers['content-type']) });
}

export async function fetchCouriers(locationId: string): Promise<string[]> {
  const { data } = await tenantApi.get('/public/me/access-couriers', { params: { location_id: locationId } });
  return unwrapList<string>(data);
}

export async function createBooking(payload: BookingPayload): Promise<BookingResult> {
  const { data } = await tenantApi.post('/public/me/access-bookings', payload, { timeout: GATE_TIMEOUT });
  return bookingResult(data);
}

export async function createGoodsRemoval({ photo, ...fields }: GoodsRemovalPayload): Promise<BookingResult> {
  const form = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    if (value) form.append(key, value);
  }
  form.append('photos', photo);

  const { data } = await tenantApi.post('/public/me/access-bookings/goods-removal', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: GATE_TIMEOUT,
  });
  return bookingResult(data);
}

export async function fetchGatePins(locationId: string): Promise<GatePin[]> {
  const { data } = await tenantApi.get('/public/me/access-pins', { params: { location_id: locationId } });
  return unwrapList<GatePin>(data);
}

export async function cancelGatePin(caseId: string, locationId: string): Promise<void> {
  await tenantApi.post(`/public/me/access-pins/${encodeURIComponent(caseId)}/cancel`, { location_id: locationId });
}

export async function fetchVisitorGroups(): Promise<VisitorGroup[]> {
  const { data } = await tenantApi.get('/public/me/visitor-groups');
  return unwrapList<VisitorGroup>(data);
}

export async function createVisitorGroup(payload: VisitorGroupPayload): Promise<VisitorGroup> {
  const { data } = await tenantApi.post('/public/me/visitor-groups', payload);
  return unwrap<VisitorGroup>(data);
}

export async function updateVisitorGroup(id: string, payload: VisitorGroupPayload): Promise<VisitorGroup> {
  const { data } = await tenantApi.put(`/public/me/visitor-groups/${encodeURIComponent(id)}`, payload);
  return unwrap<VisitorGroup>(data);
}

export async function deleteVisitorGroup(id: string): Promise<void> {
  await tenantApi.delete(`/public/me/visitor-groups/${encodeURIComponent(id)}`);
}

/** One PIN per person, made one after the other; a large group takes a while. */
export async function sendVisitorGroup(
  id: string,
  payload: { location_id: string; visit_date: string },
): Promise<{ sent: string[]; failed: { name: string; error: string }[] }> {
  const { data } = await tenantApi.post(`/public/me/visitor-groups/${encodeURIComponent(id)}/send`, payload, {
    timeout: 300_000,
  });
  return unwrap(data);
}

export async function fetchGateRegular(contactId: string, locationId: string): Promise<GateRegular | null> {
  const { data } = await tenantApi.get(`/public/me/member-contacts/${encodeURIComponent(contactId)}/gate-regular`, {
    params: { location_id: locationId },
    timeout: GATE_TIMEOUT,
  });
  return unwrap<GateRegular | null>(data);
}

export async function saveGateRegular(contactId: string, payload: GateRegularPayload): Promise<GateRegular> {
  const { data } = await tenantApi.put(
    `/public/me/member-contacts/${encodeURIComponent(contactId)}/gate-regular`,
    payload,
    { timeout: GATE_TIMEOUT },
  );
  return unwrap<GateRegular>(data);
}

export async function removeGateRegular(contactId: string, locationId: string): Promise<void> {
  await tenantApi.delete(`/public/me/member-contacts/${encodeURIComponent(contactId)}/gate-regular`, {
    params: { location_id: locationId },
    timeout: GATE_TIMEOUT,
  });
}

/** Open the gate with the QR code scanned on it. */
export async function openGate(locationId: string, barcode: string): Promise<void> {
  await tenantApi.post('/public/me/access-gate/pulse', { location_id: locationId, barcode });
}
